/**
 * 数据转换器集合
 * 每个转换器负责将原始数据转换成对应渲染组件需要的格式
 */

import type {
  GetPreviewUrlData,
  ManagePlanData,
  MediaGenerationData,
  RawToolCallData,
  WriteFileData,
} from './const';
import type { WebSearchData } from './WebSearch/const';
import type { ReferenceImage, ReferenceUIToolData } from '@/components/MessageList/ReferenceUIMessage/const';
import { REFERENCE_UI_DEFAULT_IMAGE_COUNT } from '@/components/MessageList/ReferenceUIMessage/const';
import { normalizeToArray, safeJSONParse } from '@/utils/json';

// ========== 文件操作类 ==========

export function transformWriteFileData(raw: RawToolCallData): WriteFileData {
  const args = safeJSONParse<any>(raw.arguments);
  const output = safeJSONParse<any>(raw.output);

  const fileInputs = normalizeToArray(args);
  const fileOutputs = normalizeToArray(output);

  const files = fileInputs.map((input, index) => ({
    file_path: input?.file_path || '',
    content: input?.content || '',
    success: fileOutputs[index]?.success,
    error: fileOutputs[index]?.error,
  }));

  return {
    files,
    isBatch: files.length > 1,
    isStreaming: raw.status === 'in_progress',
  };
}

// ========== 搜索与研究类 ==========

export function transformWebSearchData(raw: RawToolCallData): WebSearchData {
  const args = safeJSONParse<any>(raw.arguments);
  const output = safeJSONParse<any>(raw.output);

  return {
    query: args?.query || output?.query || '',
    answer: output?.answer,
    results: output?.results || [],
    isStreaming: raw.status === 'in_progress',
  };
}

// ========== 项目管理类 ==========

export function transformManagePlanData(raw: RawToolCallData): ManagePlanData {
  const output = safeJSONParse<any>(raw.output);
  const tasks = output?.tasks || [];
  const inProgressIndex = tasks.findIndex((t: any) => t.status === 'in_progress');

  return {
    tasks,
    summary: output?.summary,
    inProgressIndex,
    isStreaming: raw.status === 'in_progress',
  };
}

export function transformGetPreviewUrlData(raw: RawToolCallData): GetPreviewUrlData {
  return {
    url: raw.output,
  };
}

// ========== 内容生成类 ==========

export function transformImageGenerationData(raw: RawToolCallData): MediaGenerationData {
  const args = safeJSONParse<any>(raw.arguments);
  const output = safeJSONParse<any>(raw.output);

  const argItems = normalizeToArray(args);
  const outputItems = normalizeToArray(output);

  const items = argItems.map((arg, index) => {
    const itemOutput = outputItems[index];
    let urls: string[] = [];

    if (Array.isArray(itemOutput)) {
      urls = itemOutput;
    } else if (itemOutput && typeof itemOutput === 'object' && 'urls' in itemOutput) {
      urls = itemOutput.urls || [];
    }

    return {
      prompt: arg?.description || '',
      aspectRatio: arg?.aspectRatio,
      sampleCount: arg?.sampleCount,
      urls: urls.length > 0 ? urls : undefined,
      videoUrl: undefined,
      success: itemOutput && typeof itemOutput === 'object' && 'success' in itemOutput ? itemOutput.success : true,
      error: itemOutput && typeof itemOutput === 'object' && 'error' in itemOutput ? itemOutput.error : undefined,
    };
  });

  return {
    items,
    type: 'image',
    isBatch: items.length > 1,
    isStreaming: raw.status === 'in_progress',
  };
}

export function transformVideoGenerationData(raw: RawToolCallData): MediaGenerationData {
  const args = safeJSONParse<any>(raw.arguments);
  const output = safeJSONParse<any>(raw.output);

  const argItems = normalizeToArray(args);
  const outputItems = normalizeToArray(output);

  const items = argItems.map((arg, index) => {
    const itemOutput = outputItems[index];
    const videoUrl = typeof itemOutput === 'string' ? itemOutput : undefined;

    return {
      prompt: arg?.prompt || '',
      aspectRatio: arg?.aspectRatio,
      sampleCount: undefined,
      urls: undefined,
      videoUrl,
      success: itemOutput && typeof itemOutput === 'object' && 'success' in itemOutput ? itemOutput.success : true,
      error: itemOutput && typeof itemOutput === 'object' && 'error' in itemOutput ? itemOutput.error : undefined,
    };
  });

  return {
    items,
    type: 'video',
    isBatch: items.length > 1,
    isStreaming: raw.status === 'in_progress',
  };
}

// ========== 风格参考类 ==========

export function transformReferenceUIData(raw: RawToolCallData): ReferenceUIToolData {
  const output = safeJSONParse<any>(raw.output);
  const outputImages = Array.isArray(output?.referenceImages) ? output.referenceImages : [];
  const progressImages = Array.isArray(raw.progressData?.referenceImages) ? raw.progressData.referenceImages : [];
  const normalizeReferenceItem = (image: ReferenceImage): ReferenceImage => {
    const item = image && typeof image === 'object' ? image as any : {};
    return {
      ...item,
      styleName: item.styleName ?? item.style_name,
      styleDisplayName: item.styleDisplayName ?? item.style_display_name,
      styleDescription: item.styleDescription ?? item.style_description ?? item.description,
      description: item.description,
      isRecommended: item.isRecommended ?? item.is_recommended,
      recommendReason: item.recommendReason ?? item.recommend_reason,
      url: item.url ?? item.imageUrl ?? item.image_url ?? '',
      total: item.total ?? REFERENCE_UI_DEFAULT_IMAGE_COUNT,
    };
  };

  const normalizeReferenceItems = (items: ReferenceImage[]): ReferenceImage[] => items.map(normalizeReferenceItem);

  const normalizedOutputImages = normalizeReferenceItems(outputImages);
  const normalizedProgressImages = normalizeReferenceItems(progressImages);

  if (normalizedOutputImages.length > 0) {
    const outputUrlSet = new Set(normalizedOutputImages.map(item => item?.url).filter(Boolean));
    const extraProgressItems = normalizedProgressImages.filter(item => item?.url && !outputUrlSet.has(item.url));

    return {
      referenceImages: [...normalizedOutputImages, ...extraProgressItems],
      isStreaming: raw.status === 'in_progress',
    };
  }

  const itemMap = new Map<string, ReferenceImage>();

  normalizedProgressImages.forEach((item) => {
    if (!item?.url) {
      return;
    }

    itemMap.set(item.url, item);
  });

  return {
    referenceImages: Array.from(itemMap.values()),
    isStreaming: raw.status === 'in_progress',
  };
}

// ========== 沙盒操作类 ==========

export function transformRestartData(raw: RawToolCallData): { toUser?: string } {
  const args = safeJSONParse<{ to_user?: string }>(raw.arguments || '{}');
  return {
    toUser: args?.to_user,
  };
}
